import { createFollowerData, unfollowUser } from "@/api/followers";
import TooltipUserNameheading from "@/components/content-comps/content-sections/content-details-section/content-tooltip-username-heading";
import Iconify from "@/components/ui/Iconify";
import PostAvatar from "@/components/ui/PostAvatar";
import StaticVideoPreview from "@/components/ui/StaticVideoPreview";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { getIdentifier } from "@/utils/helpers";
import { useTranslations } from "@/lib/static-intl";
import Image from "next/image";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { CLOUDFRONT_BASE_URL } from "../../../../../../../config";
import { likePlacePost, unlikePlacePost } from "@/api/places";
import { Rating } from "@mui/material";

const PDW_ReviewsTilesSection = ({
  post,
  isCssForDialog,
  userFollowing,
  setUserFollowing,
  handleContentModalOpen,
  fetchPlacePosts,
}) => {
  const t = useTranslations("places.place_details_window");
  const { user, openLoginModal } = useAuthContext();
  const [postMedia] = useState([...(post?.media || [])]);

  const scrollRef = useRef(null);
  const [isScrollable, setIsScrollable] = useState(false);

  const [isLiked, setIsLiked] = useState(
    post?.like?.some((l) => l?.userId === user?.userId) || false
  );
  const [totalLikes, setTotalLikes] = useState(post?._count?.like || 0);

  const renderPostMedia = (media, index) => {
    if (media?.mediaType?.includes("video")) {
      return (
        <StaticVideoPreview
          src={`${CLOUDFRONT_BASE_URL}/${media?.filePath}`}
          className={`w-full h-full object-cover ${
            index === 0 && "rounded-l-xl"
          } ${index + 1 === postMedia?.length && "rounded-r-xl"}`}
        />
      );
    } else {
      return (
        <Image
          src={
            media?.filePath
              ? `${CLOUDFRONT_BASE_URL}/${media?.filePath}`
              : "/person.png"
          }
          alt="food"
          fill
          sizes="(max-width: 768px) 100vw,(max-width: 1200px) 50vw,33vw"
          className={`object-cover ${index === 0 && "rounded-l-xl"} ${
            index + 1 === postMedia?.length && "rounded-r-xl"
          }`}
        />
      );
    }
  };

  const scrollLeft = () => {
    if (scrollRef?.current) {
      scrollRef?.current?.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollRef?.current) {
      scrollRef?.current?.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  const handlePostLikeDislike = async () => {
    try {
      if (!user?.userId) {
        openLoginModal();
        return;
      }
      const _isLiked = isLiked;
      setIsLiked(!_isLiked);
      setTotalLikes((_t) => (_isLiked ? _t - 1 : _t + 1));
      if (_isLiked) {
        await unlikePlacePost({
          postId: post?.id || post?._id,
          userId: user?.userId,
        });
        fetchPlacePosts();
      } else {
        await likePlacePost({
          postId: post?.id || post?._id,
          userId: user?.userId,
        });
        fetchPlacePosts();
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    }
  };

  useEffect(() => {
    const checkScrollable = () => {
      if (scrollRef.current) {
        const { scrollWidth, clientWidth } = scrollRef.current;
        setIsScrollable(scrollWidth > clientWidth);
      }
    };

    checkScrollable();

    window.addEventListener("resize", checkScrollable);
    return () => window.removeEventListener("resize", checkScrollable);
  }, [postMedia]);
  return (
    <div
      className={`w-full flex flex-col gap-2 ${
        isCssForDialog ? "px-0 py-3" : "px-2 py-3"
      } relative`}
    >
      <div className="w-full flex items-center justify-between">
        <TooltipUserNameheading username={post?.user?.username}>
          <div
            className="w-fit flex items-center gap-2 cursor-pointer"
            onClick={() =>
              window.open(
                getIdentifier(post?.user?.username, post?.user?.id),
                "_blank"
              )
            }
          >
            <PostAvatar
              className={"!h-8 !min-w-8"}
              src={post?.user?.profilePicture}
            />
            <div className="w-fit flex flex-col">
              <span className="font-semibold">{post?.user?.username}</span>
              <div
                className={`w-full flex items-center ${
                  isCssForDialog ? "gap-1" : "gap-2"
                } tracking-tight text-xs text-black/60 dark:text-white/60 text-nowrap`}
              >
                <span>
                  {t("review")} {post?.user?._count?.post || 0}
                </span>
                |{" "}
                <span>
                  {t("likes")} {post?.user?._count?.likes || 0}
                </span>
                |{" "}
                <span>
                  {t("followers")} {post?.user?._count?.followers || 0}
                </span>
              </div>
            </div>
          </div>
        </TooltipUserNameheading>
        {user?.userId !== post?.userId && (
          <UserFollowBtn
            post={post}
            userFollowing={userFollowing}
            setUserFollowing={setUserFollowing}
          />
        )}
      </div>
      <div
        ref={scrollRef}
        className="w-full max-w-full overflow-x-auto rounded-xl overflow-hidden flex gap-0.5 relative group"
      >
        {/* Render Scroll Buttons Only When Scrollable */}
        {isScrollable && (
          <>
            <button
              type="button"
              className="absolute invisible group-hover:visible top-1/2 right-3 bg-gray-300 text-black/80 hover:text-black border rounded-full z-30 transform -translate-y-1/2"
              onClick={scrollRight}
            >
              <Iconify
                icon={"lsicon:right-filled"}
                className={"!text-inherit !w-5 !h-5"}
              />
            </button>
            <button
              type="button"
              className="absolute invisible group-hover:visible top-1/2 left-3 bg-gray-300 text-black/80 hover:text-black border rounded-full z-30 -translate-y-1/2"
              onClick={scrollLeft}
            >
              <Iconify
                icon={"lsicon:left-filled"}
                className={"!text-inherit !w-5 !h-5"}
              />
            </button>
          </>
        )}
        {postMedia.map((_fm, index) => (
          <div
            key={index}
            className={`relative ${
              _fm?.mediaType?.includes("video")
                ? "w-full aspect-video"
                : "aspect-square"
            } ${isCssForDialog ? "h-[180px]" : "h-[240px]"}`}
            onClick={(e) => handleContentModalOpen(e, post)}
          >
            <div
              className={`absolute w-full h-full top-0 left-0 z-10 bg-black/0 hover:bg-black/20 transition ease-in-out duration-300 cursor-pointer ${
                index === 0 && "rounded-l-xl"
              } ${index + 1 === postMedia?.length && "rounded-r-xl"}`}
            />
            {_fm?.mediaType?.includes("video") && (
              <div className="absolute top-2 right-2 w-5 h-5 bg-black/10 backdrop-blur rounded-full z-10">
                <Iconify icon={"mynaui:play-solid"} className={"!text-white"} />
              </div>
            )}
            {renderPostMedia(_fm, index)}
          </div>
        ))}
      </div>
      <p className="leading-snug tracking-tight text-sm">
        <b>{post?.title}</b> {post?.description}
      </p>
      <div className="w-full flex flex-col gap-1">
        <div className="w-full flex items-center justify-between gap-2">
          {isLiked ? (
            <button
              type="button"
              onClick={handlePostLikeDislike}
              className="flex items-center justify-center gap-1"
            >
              <Iconify icon={"mdi:like"} className={"!text-red"} />
              <span className="text-xs">{totalLikes > 0 ? totalLikes : 0}</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handlePostLikeDislike}
              className="flex items-center justify-center gap-1"
            >
              <Iconify icon={"mdi:like-outline"} className={"!opacity-60"} />
              <span className="text-xs">{totalLikes > 0 ? totalLikes : 0}</span>
            </button>
          )}
          <span className="text-black/40 dark:text-white/60 text-xs">
            {post?.createdAt?.split("T")[0]}
          </span>
        </div>
        {!!post?.rating && !isNaN(Number(post?.rating)) && (
          <Rating
            name="read-only"
            value={Number(post?.rating)}
            readOnly
            size="small"
          />
        )}
      </div>
    </div>
  );
};

export default PDW_ReviewsTilesSection;

const UserFollowBtn = ({ post, userFollowing, setUserFollowing }) => {
  const t = useTranslations("places.place_details_window");
  const apiT = useTranslations("api");
  const { user, openLoginModal } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(
    userFollowing?.find((_f) => _f?.userId === post?.userId)
  );

  const handleFollowUnfollow = async () => {
    if (!user) {
      openLoginModal();
      return;
    }
    try {
      const isUserAlreadyFollowing = userFollowing?.find(
        (_f) => _f?.userId === post?.userId
      );
      const payload = {
        followerId: user?.userId,
        followingId: post?.userId,
      };

      if (
        payload?.followerId === payload?.followingId ||
        !payload?.followerId ||
        !payload?.followingId
      ) {
        return;
      }

      setLoading(true);

      if (isUserAlreadyFollowing) {
        setUserFollowing(
          userFollowing?.filter((_f) => _f?.userId !== post?.userId)
        );
        unfollowUser(payload);
      } else {
        setUserFollowing([
          ...userFollowing,
          {
            id: Math.random(),
            userId: post?.userId,
          },
        ]);
        createFollowerData(payload);
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        {
          variant: "error",
        }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setIsFollowing(userFollowing?.find((_f) => _f?.userId === post?.userId));
  }, [userFollowing]);
  return (
    <button
      type="button"
      onClick={handleFollowUnfollow}
      disabled={loading}
      className={`${
        isFollowing
          ? "border text-black dark:text-white/80"
          : "bg-red text-white"
      } rounded-full px-4 py-1 text-sm flex items-center justify-center`}
    >
      {loading && (
        <Iconify
          icon={"line-md:loading-loop"}
          className={"!text-inherit !min-w-4 !h-4"}
        />
      )}
      {isFollowing ? t("following") : t("follow")}
    </button>
  );
};
